import { createRouter, useNavigate } from "@tanstack/react-router";
import { Compass } from "lucide-react";
import type { JSX } from "react";
import { Button, EmptyState } from "../design";
import { routeTree } from "../routeTree.gen";

/**
 * Typed selection repair for unknown deep links: when a path matches no
 * destination the shell keeps its chrome and renders this state in the
 * content region, stating why and offering the deterministic way back.
 * Scope search params survive the repair navigation.
 */
function ShellNotFound(): JSX.Element {
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={<Compass size={20} />}
      label="destination not found"
      title="This address matches no Studio destination."
      action={
        <Button
          variant="secondary"
          onClick={() => {
            void navigate({ to: "/", search: (previous) => previous, replace: true });
          }}
        >
          Back to Overview
        </Button>
      }
    >
      The link may predate this build or point at a surface this backend does not declare. The
      selection was repaired to the shell; your scope is preserved.
    </EmptyState>
  );
}

export const router = createRouter({
  routeTree,
  // Mirrors the vite `base`: "/" everywhere except the management-plane
  // release image, whose bundle serves same-origin under /studio.
  basepath: import.meta.env.VITE_JACQOS_WEB_STUDIO_ASSET_BASE ?? "/",
  defaultPreload: "intent",
  defaultNotFoundComponent: ShellNotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
